// src/components/SoundSettings.tsx
// Sound and haptics settings component for the Profile/Settings page

import React, { useState, useEffect } from 'react';
import {
  IonList,
  IonListHeader,
  IonLabel,
  IonItem,
  IonToggle,
  IonRange,
  IonText,
  IonIcon,
} from '@ionic/react';
import { volumeHighOutline, volumeMuteOutline, chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { soundService } from '../services/soundService';

export const SoundSettings: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [settings, setSettings] = useState(soundService.getSettings());

  // Update local state when settings change
  useEffect(() => {
    setSettings(soundService.getSettings());
  }, []);

  const handleSoundToggle = () => {
    const newValue = !settings.soundEnabled;
    soundService.setSoundEnabled(newValue);
    setSettings({ ...settings, soundEnabled: newValue });
  };

  const handleHapticsToggle = () => {
    const newValue = !settings.hapticsEnabled;
    soundService.setHapticsEnabled(newValue);
    setSettings({ ...settings, hapticsEnabled: newValue });
  };

  const handleVolumeChange = (value: number) => {
    soundService.setVolume(value);
    setSettings({ ...settings, volume: value });
  };

  return (
    <IonList>
      <IonItem
        button
        onClick={() => setIsExpanded(!isExpanded)}
        detail={false}
      >
        <IonIcon icon={settings.soundEnabled ? volumeHighOutline : volumeMuteOutline} slot="start" />
        <IonLabel>
          <h2>Sound & Haptics</h2>
          <p>Control sound effects and haptic feedback</p>
        </IonLabel>
        <IonIcon
          icon={isExpanded ? chevronUpOutline : chevronDownOutline}
          slot="end"
        />
      </IonItem>

      {isExpanded && (
        <>
          {/* Sound Effects Toggle */}
          <IonItem>
            <IonLabel>
              <h2>Sound Effects</h2>
              <p>Play sound effects throughout the app</p>
            </IonLabel>
            <IonToggle
              checked={settings.soundEnabled}
              onIonChange={handleSoundToggle}
              aria-label="Toggle sound effects"
            />
          </IonItem>

          {/* Volume Slider */}
          {settings.soundEnabled && (
            <IonItem>
              <IonLabel>
                <h2>Volume</h2>
                <p>Adjust sound effect volume</p>
              </IonLabel>
              <IonRange
                min={0}
                max={1}
                step={0.1}
                value={settings.volume}
                onIonChange={(e) => handleVolumeChange(e.detail.value as number)}
                pin={true}
                pinFormatter={(value: number) => `${Math.round(value * 100)}%`}
                aria-label="Adjust volume"
                style={{ paddingTop: '10px' }}
              >
                <IonIcon icon={volumeMuteOutline} slot="start" size="small" />
                <IonIcon icon={volumeHighOutline} slot="end" size="small" />
              </IonRange>
            </IonItem>
          )}

          {/* Haptic Feedback Toggle */}
          <IonItem>
            <IonLabel>
              <h2>Haptic Feedback</h2>
              <p>Vibration feedback for interactions (mobile only)</p>
            </IonLabel>
            <IonToggle
              checked={settings.hapticsEnabled}
              onIonChange={handleHapticsToggle}
              aria-label="Toggle haptic feedback"
            />
          </IonItem>

          <IonItem lines="none">
            <IonText color="medium" style={{ fontSize: '0.875rem', padding: '8px 0' }}>
              <p>
                Sound and haptic settings apply to all games and interactions
              </p>
            </IonText>
          </IonItem>
        </>
      )}
    </IonList>
  );
};
